import {fuzzyScore} from '@github-ui/fuzzy-score/fuzzy-score'
import type {SafeHTMLString} from '@github-ui/safe-html'

import {DEFAULT_FILTER_PRIORITY, FILTER_KEYS, INDETERMINANT} from '../constants/filter-constants'
import type {FilterQuery} from '../filter-query'
import {
  type AnyBlock,
  type ARIAFilterSuggestion,
  type FilterConfig,
  type FilterProvider,
  FilterProviderType,
  type FilterValueData,
  FilterValueType,
  type IndexedBlockValueItem,
  type MutableFilterBlock,
  type QueryContext,
  type SuppliedFilterProviderOptions,
  type UnrankedARIAFilterSuggestion,
  type ValueRowProps,
} from '../types'
import {getDefaultValueVisualDescriptors, getFilterValue, getUnescapedFilterValue} from '../utils'
import {AsyncFilterProvider} from './async'

const LABEL_SUGGESTIONS_ENDPOINT = '/_filter/labels'
const LABEL_VALIDATION_ENDPOINT = '/_filter/labels/validate'

export type Label = {
  name: string
  nameHtml?: SafeHTMLString
  description?: string
  color?: string
  archivedAt?: string
}

export class LabelFilterProvider extends AsyncFilterProvider<Label> implements FilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.label, options)
    this.suggestionEndpoint = LABEL_SUGGESTIONS_ENDPOINT
    this.validationEndpoint = LABEL_VALIDATION_ENDPOINT
    this.type = FilterProviderType.Select
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.processSuggestion.bind(this), caretIndex)
  }

  getIconData(label: Label, context: QueryContext) {
    if (!context['repo']) {
      return {icon: this.icon}
    } else {
      const iconColor = label.color?.startsWith('#') ? label.color : `#${label.color}`
      return {iconColor, description: label.description, inlineDescription: false}
    }
  }

  override rankSuggestion(query: string, suggestion: UnrankedARIAFilterSuggestion): ARIAFilterSuggestion {
    const {value} = suggestion
    const name = getUnescapedFilterValue(value) || ''
    let priority = DEFAULT_FILTER_PRIORITY

    if (query && name) priority -= fuzzyScore(query, name)

    return {...suggestion, priority}
  }

  protected processSuggestion(label: Label, query: string, filterQuery: FilterQuery): ARIAFilterSuggestion {
    return this.rankSuggestion(query, {
      type: FilterValueType.Value,
      displayName: label.name,
      displayNameHtml: label.nameHtml,
      ariaLabel: `${label.name}, ${this.displayName}`,
      value: label.name ?? '',
      ...this.getIconData(label, filterQuery.context),
    })
  }

  override validateValue(
    filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    label: Label | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!extractedValue) return false

    if (label)
      return {
        iconColor: label.color?.startsWith('#') ? label.color : `#${label.color}`,
        value: extractedValue,
        displayName: label.name,
        ...this.getIconData(label, filterQuery.context),
      }

    // When no repo context exists (for example, org-level or global context), treat as indeterminate
    // because we can't validate against all possible repositories performantly
    const isIndeterminate = !filterQuery.context.repo

    if (isIndeterminate) {
      return {
        value: INDETERMINANT,
      }
    }

    return false
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    const customLeadingVisual = this.options.renderValueVisual?.(value)
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      descriptionVariant: 'block',
      leadingVisual: this.options.renderValueVisual
        ? (customLeadingVisual ?? null)
        : getDefaultValueVisualDescriptors(value, this.icon),
    }
  }
}
